import React from "react";
import { ArrowLeft, Check, Send, Users, Calendar, Flame } from "lucide-react";
import { DetailContainer, BackButton } from "./UI";
import { getCategoryById } from "../services/CategoryModel";
import { getAvatarUrl } from "../hooks/useAvatarUtils";
import { useAppData } from "../context/data";
import styles from "./Events.module.css";

// Read-only profile view for another attendee: shown when the user taps
// someone in an event's attendee list or an invite candidate card. Pulls
// the full communities/events lists from DataContext to compute which ones
// this specific attendee belongs to/created, since only their basic user
// object (not their derived memberships) is passed in as a prop.
export default function AttendeeProfile({
  user,
  event,
  isInvited = false,
  onBack,
  onInvite,
  onSelectEvent,
}) {
  const { communities, events } = useAppData();

  // Filter down to the communities/events this specific user participates in
  const userCommunities = communities.filter((c) =>
    user?.joinedCommunities?.includes(c.id)
  );

  const userEvents = events.filter(
    (e) =>
      e?.createdBy === (user?.id || user?.uid) ||
      user?.joinedEvents?.includes(e?.id)
  );

  return (
    <DetailContainer>
      <div className={styles.topBar}>
        <BackButton onClick={onBack}>
          <ArrowLeft size={20} />
        </BackButton>
        <span className={styles.categoryBadge}>
          <Flame size={12} /> Member Profile
        </span>
      </div>

      <div className={styles.profileViewContainerPadded}>
        <div className={styles.profileAvatarWrapper}>
          <img
            src={getAvatarUrl(user)}
            alt={user.name || "User"}
            className={styles.profileAvatarImg}
          />
        </div>

        <h2 className={styles.profileName}>{user.name || "User"}</h2>
        <p className={styles.profileSubtitle}>
          {user.role || "Meetly Community Member"}
        </p>

        <p className={styles.profileBio}>
          {user.bio ||
            "No biography provided yet. Passionate about connecting, networking, and exploring activities across Dublin."}
        </p>

        {/* Interests section */}
        <div className={styles.profileInterestsSection}>
          <h4 className={styles.profileInterestsTitle}>Interests & Passions</h4>
          <div className={styles.profileTagsContainer}>
            {user.interests && user.interests.length > 0 ? (
              user.interests.map((tagId) => {
                const tagCat = getCategoryById(tagId);
                return (
                  <span key={tagId} className={styles.profileTag}>
                    {tagCat.icon} {tagCat.label}
                  </span>
                );
              })
            ) : (
              <p className={styles.statusMessage}>No interests listed yet.</p>
            )}
          </div>
        </div>

        {/* Communities section */}
        <div className={styles.profileInterestsSection}>
          <h4 className={styles.profileInterestsTitle}>
            <Users size={13} className={styles.inlineIcon} /> Communities Joined
          </h4>
          <div className={styles.listColumn}>
            {userCommunities.length > 0 ? (
              userCommunities.map((comm) => (
                <div key={comm.id} className={styles.listRow}>
                  <img
                    src={comm.image}
                    alt={comm.name}
                    className={styles.listThumb}
                  />
                  <div className={styles.listTextBlock}>
                    <p className={styles.listTitle}>{comm.name}</p>
                    <p className={styles.listSubtitle}>
                      {comm.members || "Community"}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className={styles.statusMessage}>No communities joined yet.</p>
            )}
          </div>
        </div>

        {/* Activities / Events section */}
        <div className={styles.profileInterestsSection}>
          <h4 className={styles.profileInterestsTitle}>
            <Calendar size={13} className={styles.inlineIcon} /> Activities &
            Events
          </h4>
          <div className={styles.listColumn}>
            {userEvents.length > 0 ? (
              userEvents.map((ev) => (
                <div
                  key={ev.id}
                  onClick={() => onSelectEvent && onSelectEvent(ev)}
                  className={styles.listRowClickable}
                  title="Click to view event"
                >
                  <img
                    src={ev.image}
                    alt={ev.title}
                    className={styles.listThumb}
                  />
                  <div className={styles.listTextBlock}>
                    <p className={styles.listTitle}>{ev.title}</p>
                    <p className={styles.listSubtitle}>{ev.date}</p>
                  </div>
                </div>
              ))
            ) : (
              <p className={styles.statusMessage}>No public events yet.</p>
            )}
          </div>
        </div>

        {/* Invite action button */}
        {event && onInvite && (
          <button
            onClick={(e) => onInvite(user, e)}
            className={
              isInvited
                ? styles.profileConnectBtnInvited
                : styles.profileConnectBtn
            }
          >
            {isInvited ? (
              <>
                <Check size={18} /> Invitation Sent
              </>
            ) : (
              <>
                <Send size={18} /> Invite to {event.title}
              </>
            )}
          </button>
        )}
      </div>
    </DetailContainer>
  );
}
